import { useTranslation } from 'react-i18next';
import * as amplitude from '@amplitude/analytics-browser';

import { Button, Tooltip } from '@unified-checkout/design-system';
import { ArrowUpRightFromSquare } from '@unified-checkout/design-system/icons/index';

import type { ISummaryCard } from '@app/dashboard/types/innerLayout/balance_and_transfers/transfers/summary';
import type { ITransferSummaryProps } from '@app/dashboard/types/innerLayout/balance_and_transfers/single-transfer/transfer-summary';
import { truncateString } from '@app/dashboard/utils/truncateString';
import { formatToLocalizedDateTime } from '@app/dashboard/utils/dateFormats';
import { EMPTY_VALUE } from '@app/dashboard/constants/shared';
import {
  BALANCE_TRANSFERS_EVENTS,
  BALANCE_TRANSFERS_EVENTS_PROPERTIES,
} from '@app/dashboard/constants/tracking_events/balance-and-transfers';
import {
  INSTANT_SETTLEMENT,
  SUMMARY_CARD_IDS_TYPES,
  TRUNCATED_SUMMARY_LENGTH,
} from '@app/dashboard/constants/balance_and_transfers/single-transfer';
import { useLangStore } from '@app/dashboard/store/lang';
import CopyText from '@app/dashboard/components/inner_layout/CopyText';

import {
  formatAmountWithCurrency,
  isEGYCountry,
  isUAECountry,
  viewRelatedTransactions,
} from '../../utils';
import {
  getSharedEventProperties,
  handleStoreLastAction,
  viewRelatedTransactionsEventTracking,
} from '../../utils/events-tracking';
import SummaryCard from '../SummaryCard';

import styles from './transfer-summary.module.scss';

const TransferSummary = ({
  isLoading,
  transferSummary,
  expectedArrival,
  status = '',
  transactionCount = 0,
}: ITransferSummaryProps) => {
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const isUAE = isUAECountry();
  const isEGY = isEGYCountry();
  const {
    amount = 0,
    bank,
    currency = '',
    destination,
    net_amount = 0,
    payment_method,
    settled_on,
    settlement_id,
    settlement_type,
    settlement_type_key,
    transfer_id = '',
    payout_id = '',
    bank_ref_no = '',
  } = transferSummary || {};
  const instantSettlement = INSTANT_SETTLEMENT === settlement_type_key;

  const getRelatedTransactions = (id: string = '') => {
    viewRelatedTransactionsEventTracking({
      currency,
      status,
      transactionCount,
      transferId: id,
    });
    viewRelatedTransactions(id);
  };

  const handleCopyTracker = (
    type: Lowercase<keyof typeof SUMMARY_CARD_IDS_TYPES>
  ) => {
    const eventName =
      type === SUMMARY_CARD_IDS_TYPES.SETTLEMENT_ID
        ? BALANCE_TRANSFERS_EVENTS.TRANSFER_DETAIL_PAGE.SETTLEMENT_ID_COPIED
        : BALANCE_TRANSFERS_EVENTS.TRANSFER_DETAIL_PAGE.TRANSFER_ID_COPIED;

    amplitude.track(eventName, {
      [BALANCE_TRANSFERS_EVENTS_PROPERTIES.SHARED.AMOUNT]: net_amount ?? amount,
      ...getSharedEventProperties({
        transferId: transfer_id,
        status,
        currency,
      }),
      ...(type === SUMMARY_CARD_IDS_TYPES.SETTLEMENT_ID && {
        [BALANCE_TRANSFERS_EVENTS_PROPERTIES.SHARED.SETTLEMENT_ID]:
          settlement_id,
      }),
    });
    handleStoreLastAction(eventName);
  };

  const SUMMARY_CARD_ITEMS: ISummaryCard['itemsList'] = [
    {
      label: t('innerLayout.balanceTransfers.drawerDetails.summary.transferId'),
      toolTipProps: {
        text: t(
          'innerLayout.balanceTransfers.drawerDetails.summary.paymobIdRefTooltip'
        ),
      },
      value: {
        renderAs: (
          <div className={styles['transfer-summary__transfer-id']}>
            <div className={styles['transfer-summary__transfer-id-actions']}>
              <CopyText
                textToCopy={transfer_id}
                buttonProps={{
                  size: 'sm',
                  color: 'primary',
                }}
                onCopyTextHandler={() =>
                  handleCopyTracker(SUMMARY_CARD_IDS_TYPES.TRANSFER_ID)
                }
              />
              <Button
                trailingIcon={ArrowUpRightFromSquare}
                variant="link"
                color="primary"
                size="sm"
                onClickHandler={() => getRelatedTransactions(transfer_id)}
                text={transfer_id}
                id="transfer-summary__transfer-id-button"
              />
            </div>
            <p className={styles['transfer-summary__transfer-id-note']}>
              {t(
                'innerLayout.balanceTransfers.drawerDetails.summary.viewRelatedTransactions'
              )}
            </p>
          </div>
        ),
      },
    },
    ...(!instantSettlement
      ? [
          {
            label: t(
              'innerLayout.balanceTransfers.drawerDetails.summary.settlementId'
            ),
            value: {
              ...(settlement_id
                ? {
                    renderAs: (
                      <div
                        className={styles['transfer-summary__settlement-id']}
                      >
                        <CopyText
                          textToCopy={settlement_id}
                          showTextToCopy
                          buttonProps={{
                            size: 'sm',
                            color: 'secondary',
                          }}
                          onCopyTextHandler={() =>
                            handleCopyTracker(
                              SUMMARY_CARD_IDS_TYPES.SETTLEMENT_ID
                            )
                          }
                        />
                      </div>
                    ),
                  }
                : {
                    text: EMPTY_VALUE,
                    isBold: true,
                  }),
            },
            toolTipProps: {
              text: t(
                'innerLayout.balanceTransfers.drawerDetails.summary.paymobIdRefTooltip'
              ),
            },
          },
        ]
      : []),
    ...(isUAE || instantSettlement
      ? [
          {
            label: t(
              'innerLayout.balanceTransfers.drawerDetails.summary.payoutId'
            ),
            value: {
              ...(payout_id
                ? {
                    renderAs: (
                      <div className={styles['transfer-summary__reference-id']}>
                        <CopyText
                          textToCopy={payout_id}
                          showTextToCopy={false}
                          buttonProps={{
                            size: 'sm',
                            color: 'secondary',
                          }}
                        />
                        <Tooltip text={payout_id}>
                          {truncateString(payout_id, TRUNCATED_SUMMARY_LENGTH)}
                        </Tooltip>
                      </div>
                    ),
                  }
                : {
                    text: EMPTY_VALUE,
                    isBold: true,
                  }),
            },
            toolTipProps: {
              text: t(
                'innerLayout.balanceTransfers.drawerDetails.summary.paymobIdRefTooltip'
              ),
            },
          },
        ]
      : []),
    ...(isUAE || (isEGY && instantSettlement)
      ? [
          {
            label: t(
              'innerLayout.balanceTransfers.drawerDetails.summary.bankRefNo'
            ),
            value: {
              ...(bank_ref_no
                ? {
                    renderAs: (
                      <div className={styles['transfer-summary__reference-id']}>
                        <CopyText
                          textToCopy={bank_ref_no}
                          showTextToCopy={false}
                          buttonProps={{
                            size: 'sm',
                            color: 'secondary',
                          }}
                        />
                        <Tooltip text={bank_ref_no}>
                          {truncateString(
                            bank_ref_no,
                            TRUNCATED_SUMMARY_LENGTH
                          )}
                        </Tooltip>
                      </div>
                    ),
                  }
                : {
                    text: EMPTY_VALUE,
                    isBold: true,
                  }),
            },
            toolTipProps: {
              text: t(
                'innerLayout.balanceTransfers.drawerDetails.summary.bankRefNoTooltip'
              ),
            },
          },
        ]
      : []),
    {
      label: t(
        'innerLayout.balanceTransfers.drawerDetails.summary.grossAmount'
      ),
      value: {
        text: formatAmountWithCurrency({
          amount,
          currency: currency,
          lang,
        }),
        isBold: true,
      },
    },
    {
      label: t(
        'innerLayout.balanceTransfers.drawerDetails.summary.settleInBank'
      ),
      value: {
        ...(amount
          ? {
              text: formatAmountWithCurrency({
                amount: net_amount,
                currency: currency,
                lang,
              }),
              isBold: true,
              status: 'success',
            }
          : {
              text: EMPTY_VALUE,
              hintText: `(${t(
                'innerLayout.balanceTransfers.drawerDetails.summary.noFunds'
              )})`,
              isBold: true,
            }),
      },
    },
    {
      label: t(
        'innerLayout.balanceTransfers.drawerDetails.summary.settlementType'
      ),
      value: {
        text: settlement_type,
        hintText: expectedArrival as string,
        isBold: true,
      },
    },
    {
      label: t(
        'innerLayout.balanceTransfers.drawerDetails.summary.paymentMethod'
      ),
      value: {
        text: payment_method,
        isBold: true,
      },
    },
    {
      label: t(
        'innerLayout.balanceTransfers.drawerDetails.summary.destination'
      ),
      value: {
        text: destination,
        isBold: true,
      },
    },
    {
      label: t('innerLayout.balanceTransfers.drawerDetails.summary.bank'),
      value: {
        text: bank,
        isBold: true,
      },
    },
    {
      label: t('innerLayout.balanceTransfers.drawerDetails.summary.settledOn'),
      value: {
        ...(settled_on
          ? {
              text: formatToLocalizedDateTime(settled_on, lang),
              isBold: true,
            }
          : {
              text: EMPTY_VALUE,
              isBold: true,
            }),
      },
    },
  ];

  const handleExpandClickTracker = (expanded: boolean) => {
    if (!expanded) return;

    const eventName =
      BALANCE_TRANSFERS_EVENTS.TRANSFER_DETAIL_PAGE.SETTLE_IN_BANK_EXPANDED;

    amplitude.track(eventName, {
      [BALANCE_TRANSFERS_EVENTS_PROPERTIES.TRANSFER_DETAIL_PAGE.SETTLE_AMOUNT]:
        net_amount,
      ...getSharedEventProperties({
        transferId: transfer_id ?? '',
        status,
        currency,
      }),
    });

    handleStoreLastAction(eventName);
  };

  return (
    <SummaryCard
      title={t('innerLayout.balanceTransfers.drawerDetails.transferSummary')}
      itemsList={SUMMARY_CARD_ITEMS}
      collapsible={{
        isExpandableCentred: true,
        showCollapseButton: true,
        visibleCount: 5,
        renderAsRow: false,
        expandableButton: {
          text: '',
          onClickHandler: handleExpandClickTracker,
        },
      }}
      key={`summary-card${transfer_id}`}
      isLoading={isLoading}
    />
  );
};

export default TransferSummary;
